import React, { useState } from 'react';
import { HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { motion, AnimatePresence } from 'framer-motion';
export function FAQ() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const faqs = en ? [{
    q: 'Warranty?',
    a: '12 months factory warranty for all legal entities, in accordance with factory standards.'
  }, {
    q: 'Service?',
    a: 'We currently have service partners in Pančevo (South Banat District) and Belgrade (Surčin). Negotiations for additional regions are ongoing.'
  }, {
    q: 'Delivery time?',
    a: 'For models not in stock, the standard lead time is 4–8 weeks from production. Contact us for current stock availability.'
  }, {
    q: 'Payment?',
    a: 'Payment via proforma invoice for legal entities. Prices are in EUR without VAT; calculation is done in RSD at the NBS middle exchange rate on the day of payment.'
  }, {
    q: 'Can I see the machine before buying?',
    a: 'Contact us via the contact form or phone for more details about the machines and viewing options.'
  }, {
    q: 'Do you deliver outside Serbia?',
    a: 'We currently deliver within the territory of Serbia. For regional orders, contact us directly.'
  }] : ru ? [{
    q: 'Гарантия?',
    a: 'Заводская гарантия 12 месяцев для всех юридических лиц в соответствии с заводскими стандартами.'
  }, {
    q: 'Сервис?',
    a: 'В настоящее время у нас есть сервисные партнеры в Панчево (Южно-Банатский округ) и Белграде (Сурчин). Ведутся переговоры по дополнительным регионам.'
  }, {
    q: 'Срок поставки?',
    a: 'Для моделей, которых нет в наличии, стандартный срок поставки составляет 4–8 недель с производства. Свяжитесь с нами для уточнения наличия на складе.'
  }, {
    q: 'Оплата?',
    a: 'Оплата по счету-проформе для юридических лиц. Цены указаны в евро без НДС; расчет производится в динарах по среднему курсу НБС на день оплаты.'
  }, {
    q: 'Могу ли я увидеть машину перед покупкой?',
    a: 'Свяжитесь с нами через контактную форму или по телефону для получения дополнительной информации о машинах и возможностях осмотра.'
  }, {
    q: 'Доставляете ли вы за пределы Сербии?',
    a: 'В настоящее время мы доставляем по территории Сербии. Для региональных заказов свяжитесь с нами напрямую.'
  }] : [{
    q: 'Garancija?',
    a: '12 meseci fabrička garancija za sva pravna lica, u skladu sa fabričkim standardima.'
  }, {
    q: 'Servis?',
    a: 'Trenutno imamo serviserske partnere u Pančevu (Južnobanatski okrug) i Beogradu (Surčin). U toku su pregovori za dodatne regione.'
  }, {
    q: 'Rok isporuke?',
    a: 'Za modele koji nisu na stanju, standardni rok je 4–8 nedelja iz proizvodnje. Za trenutno stanje lagera kontaktirajte nas.'
  }, {
    q: 'Plaćanje?',
    a: 'Plaćanje po profakturi za pravna lica. Cene su u EUR bez PDV-a; obračun se vrši u dinarima po srednjem kursu NBS na dan plaćanja.'
  }, {
    q: 'Da li mogu da vidim mašinu pre kupovine?',
    a: 'Kontaktirajte nas preko kontakt forme ili telefonom za više informacija o mašinama i mogućnostima razgledanja.'
  }, {
    q: 'Da li isporučujete van Srbije?',
    a: 'Trenutno isporučujemo na teritoriji Srbije. Za regionalne porudžbine kontaktirajte nas direktno.'
  }];
  return <section className="py-16 md:py-20 bg-dark-bg border-b-2 border-dark-border" data-id="element-214">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
        <div className="text-center mb-10 md:mb-12" data-id="element-216">
          <div className="w-14 h-14 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-217">
            <HelpCircleIcon className="w-7 h-7 text-brand-yellow" data-id="element-218" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2" data-id="element-219">
            {en ? 'Frequently Asked Questions' : ru ? 'Часто задаваемые вопросы' : 'Često postavljana pitanja'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-220">
          {faqs.map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-221" />)}
        </div>
      </div>
    </section>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className={`bg-dark-surface border rounded-industrial overflow-hidden transition-colors duration-200 ${isOpen ? 'border-brand-yellow/60' : 'border-dark-border hover:border-brand-yellow/40'}`} data-id="element-222">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-5 py-3.5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-brand-yellow gap-3" data-id="element-223">
        <span className="text-base font-bold text-white" data-id="element-224">{question}</span>
        <ChevronDownIcon className={`w-5 h-5 text-brand-yellow flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-225" />
      </button>
      <AnimatePresence data-id="element-226">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-227">
            <div className="px-7 pt-5 pb-7 text-sm md:text-base text-gray-400 leading-relaxed" data-id="element-228">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}